import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams } from "react-router-dom";
import { getAmenityCategoryById } from "../../../../../../../apis/property";
import {
  XIcon,
  PencilAltIcon,
  TagIcon,
  DocumentTextIcon,
  RefreshIcon,
} from "@heroicons/react/outline";

const EditAmenityCategory = ({
  isOpen,
  onClose,
  selectedAmenityCategoryId,
}) => {
  const { id } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  useEffect(() => {
    const fetchAmenityCategory = async () => {
      if (selectedAmenityCategoryId && isOpen) {
        setLoading(true);
        try {
          const response = await getAmenityCategoryById(id, selectedAmenityCategoryId);
          setFormData({
            name: response.data.name,
            description: response.data.description,
          });
        } catch (error) {
          console.log("Error fetching amenity category");
          toast.error("Failed to load amenity category");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAmenityCategory();
  }, [id, selectedAmenityCategoryId, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${baseUrl}/resort/${encodeURIComponent(id)}/amenity/category/${selectedAmenityCategoryId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        toast.success("Amenity category updated successfully");
        onClose();
      } else {
        toast.error("Failed to update amenity category");
        console.error("Failed to update amenity category");
      }
    } catch (error) {
      toast.error("Error occurred while updating amenity category");
      console.error("Error occurred while updating amenity category:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="relative w-full max-w-lg transform transition-all">
            <div className="modal-container">
              {/* Header */}
              <div className="modal-header">
                <div className="flex items-center space-x-3">
                  <div className="modal-icon">
                    <PencilAltIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="modal-title">Edit Amenity Category</h3>
                    <p className="modal-subtitle">Update category information</p>
                  </div>
                </div>
                <button onClick={onClose} className="modal-close-btn" type="button">
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Loading State */}
              {loading ? (
                <div className="loading-container">
                  <RefreshIcon className="loading-spinner" />
                  <p className="loading-text">Loading category details...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {/* Category Name */}
                    <div className="form-group">
                      <label htmlFor="name" className="form-label">
                        <TagIcon className="form-label-icon" />
                        Category Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., Pool & Spa, Dining, Entertainment"
                        required
                      />
                    </div>

                    {/* Description */}
                    <div className="form-group">
                      <label htmlFor="description" className="form-label">
                        <DocumentTextIcon className="form-label-icon" />
                        Description *
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="form-textarea"
                        placeholder="Describe this amenity category..."
                        rows="4"
                        required
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="modal-footer">
                    <button
                      type="button"
                      onClick={onClose}
                      className="btn-secondary"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Updating...
                        </>
                      ) : (
                        "Update Category"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .modal-container {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: linear-gradient(135deg, #f59e0b, #f97316);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .loading-container {
          padding: 4rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .loading-spinner {
          width: 3rem;
          height: 3rem;
          color: #f59e0b;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .loading-text {
          margin-top: 1rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .modal-body {
          padding: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .form-label-icon {
          width: 1rem;
          height: 1rem;
          margin-right: 0.5rem;
          color: #f59e0b;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #f9fafb;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #f59e0b;
          background: white;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .btn-secondary {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .btn-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #f59e0b, #f97316);
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #d97706, #ea580c);
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(245, 158, 11, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default EditAmenityCategory;
